/**
 * Chutt... V2 - Agir Page (6 Tabs + Workflow + SEO + Press CRM)
 * Tab 0: Plan d'Action -- Grouped by mission, Accordion per action, workflow badges
 * Tab 1: Creer du Contenu -- Expert RP at top + ContentGenerator
 * Tab 2: Effacer du Contenu -- Templates IA (droit a l'oubli, RGPD)
 * Tab 3: Gerer SEO/SERP -- SERP scan results, boost/suppress campaigns
 * Tab 4: Repondre aux Mentions -- Recent mentions with AI reply generation
 * Tab 5: Relations Presse -- CRM table + add/edit dialog
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import ReplyIcon from '@mui/icons-material/Reply';
import ContactsIcon from '@mui/icons-material/Contacts';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LanguageIcon from '@mui/icons-material/Language';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';

import ContentGenerator from '../components/agir/ContentGenerator';
import GlassCard from '../components/shared/GlassCard';
import ScrollReveal from '../components/shared/ScrollReveal';
import LastScanLabel from '../components/shared/LastScanLabel';
import CollapsibleSection from '../components/shared/CollapsibleSection';
import ConfirmDialog from '../components/shared/ConfirmDialog';
import { agirAPI } from '../services/apiV2';
import { seoAPI, objectivesAPI, missionsAPI, monitoringAPI } from '../services/api';
import { colors } from '../styles/colors';
import { getDailyCitation } from '../utils/dailyCitation';

const DATA_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 heures

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

const MISSION_LABELS = {
  protect: 'Proteger',
  visibility: 'Rayonner',
  trust: 'Confiance',
  community: 'Federer',
  _other: 'Autres actions',
};

const MISSION_INFO = {
  protect: 'Surveillez et neutralisez les risques reputationnels avant qu\'ils n\'impactent votre image.',
  visibility: 'Augmentez votre presence mediatique et digitale pour gagner en notoriete.',
  trust: 'Consolidez la perception positive de votre marque aupres de vos parties prenantes.',
  community: 'Engagez et fidelisez votre audience pour creer un reseau d\'ambassadeurs.',
};

const WORKFLOW_STATUS_CONFIG = {
  pending: { label: 'A faire', color: colors.gray400, bg: 'rgba(155,168,162,0.1)' },
  in_progress: { label: 'En cours', color: colors.info, bg: 'rgba(59,130,246,0.1)' },
  review: { label: 'A valider', color: colors.warning, bg: 'rgba(245,158,11,0.1)' },
  completed: { label: 'Termine', color: colors.success, bg: 'rgba(34,197,94,0.1)' },
};

const DELETE_TEMPLATES = [
  {
    id: 'google_right_to_be_forgotten',
    title: 'Droit a l\'oubli Google',
    description: 'Demande de suppression de resultats de recherche Google (article 17 RGPD). Applicable aux personnes physiques dans l\'UE.',
    icon: <LanguageIcon />,
    type: 'Formulaire Google',
  },
  {
    id: 'rgpd_data_deletion',
    title: 'Suppression donnees RGPD',
    description: 'Demande de suppression de donnees personnelles aupres d\'un site web ou service en ligne (article 17 RGPD).',
    icon: <GavelIcon />,
    type: 'Lettre RGPD',
  },
  {
    id: 'platform_report',
    title: 'Signalement plateforme',
    description: 'Signalement de contenu diffamatoire ou trompeur aupres des reseaux sociaux (Facebook, Twitter, LinkedIn, Instagram).',
    icon: <FlagIcon />,
    type: 'Signalement',
  },
  {
    id: 'legal_takedown',
    title: 'Mise en demeure',
    description: 'Lettre de mise en demeure pour suppression de contenu portant atteinte a la reputation. A faire valider par un avocat.',
    icon: <DescriptionIcon />,
    type: 'Juridique',
  },
];

function normalizePlatform(raw) {
  if (!raw) return '';
  const lower = raw.toLowerCase().trim();
  if (lower === 'linkedin') return 'linkedin';
  if (lower === 'twitter' || lower === 'x') return 'twitter';
  if (lower === 'facebook' || lower === 'fb') return 'facebook';
  if (lower === 'instagram' || lower === 'ig') return 'instagram';
  if (lower === 'blog' || lower === 'site') return 'blog';
  return lower;
}

function detectContentType(actionTitle, platform) {
  if (!actionTitle) return platform === 'twitter' ? 'tweet' : 'post_linkedin';
  const lower = actionTitle.toLowerCase();
  if (lower.includes('linkedin')) return 'post_linkedin';
  if (lower.includes('tweet') || lower.includes('twitter')) return 'tweet';
  if (lower.includes('communiqu') || lower.includes('presse')) return 'communique';
  if (lower.includes('article') || lower.includes('blog')) return 'article_blog';
  if (lower.includes('repon') || lower.includes('commentaire')) return 'reponse_commentaire';
  if (platform === 'linkedin') return 'post_linkedin';
  if (platform === 'twitter') return 'tweet';
  return 'post_linkedin';
}

// Action type → display label + tab index for "Go to" button
const ACTION_TYPE_LABELS = {
  creer_contenu: { label: 'Generer contenu', tab: 1 },
  effacer_contenu: { label: 'Effacer contenu', tab: 2 },
  seo_serp: { label: 'Gerer SEO', tab: 3 },
  repondre_mentions: { label: 'Repondre', tab: 4 },
  relations_presse: { label: 'Relations', tab: 5 },
  planifier_publication: { label: 'Planifier', tab: 1 },
};

function AgirPage() {
  const { selectedBrand, brands, cacheVersion, lastUpdated } = useOutletContext();

  const [activeTab, setActiveTab] = useState(0);
  const [actions, setActions] = useState([]);
  const [, setObjectives] = useState([]);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);


  // Expert RP
  const [expertRP, setExpertRP] = useState(null);
  const [expertLoading, setExpertLoading] = useState(false);

  // Content generation
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatedVisual, setGeneratedVisual] = useState(null);
  const [contentPrefill, setContentPrefill] = useState({ topic: '', platform: '', contentType: '' });
  const [generatingStep, setGeneratingStep] = useState('');

  // Copied message state
  const [copiedId, setCopiedId] = useState(null);

  // Exclusive accordion for missions
  const [openSection, setOpenSection] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterPriority, setFilterPriority] = useState('all');

  // Brand image
  const [brandImageUrl, setBrandImageUrl] = useState(null);

  // Workflow status change menu
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuActionId, setStatusMenuActionId] = useState(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // SEO/SERP state (Tab 3)
  const [serpResults, setSerpResults] = useState(null);
  const [serpLoading, setSerpLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  // Mentions for reply (Tab 4)
  const [recentMentions, setRecentMentions] = useState([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [replyGenerating, setReplyGenerating] = useState(null);
  const [generatedReplies, setGeneratedReplies] = useState({});

  // Influence mapping (Tab 5 — discovery panel)
  const [influencers, setInfluencers] = useState([]);
  const [influenceLoading, setInfluenceLoading] = useState(false);

  // Press Contacts (Tab 5)
  const [pressContacts, setPressContacts] = useState([]);
  const [pressLoading, setPressLoading] = useState(false);
  const [pressDialogOpen, setPressDialogOpen] = useState(false);
  const [pressEditId, setPressEditId] = useState(null);
  const [pressForm, setPressForm] = useState({ name: '', media: '', email: '', phone: '', notes: '' });

  // Delete content template generation (Tab 2)
  const [generatingTemplate, setGeneratingTemplate] = useState(null);
  const [generatedTemplates, setGeneratedTemplates] = useState({});

  const brandName = brands?.find(b => b.id === selectedBrand)?.name || '';
  const profileType = brands?.find(b => b.id === selectedBrand)?.profile_type || 'organisation';

  // ========= DATA FETCHING =========

  const actionsCacheKey = `agir_actions_${selectedBrand}`;

  const fetchData = useCallback(async (force = false) => {
    if (!selectedBrand) return;

    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(actionsCacheKey);
        if (cached) {
          const { actions: cActions, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DATA_CACHE_TTL) {
            setActions(cActions || []);
            setLoading(false);
            loadedOnce.current = true;
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }

    if (!loadedOnce.current) setLoading(true);
    try {
      const [actionsRes, objectivesRes] = await Promise.all([
        agirAPI.getActionPlan(selectedBrand).catch(() => ({ data: { actions: [] } })),
        objectivesAPI.getProgress(selectedBrand).catch(() => ({ data: { objectives: [] } })),
      ]);
      const actionsData = actionsRes.data?.actions || [];
      setActions(actionsData);
      setObjectives(objectivesRes.data?.objectives || []);

      // Save to localStorage cache
      try {
        localStorage.setItem(actionsCacheKey, JSON.stringify({ actions: actionsData, timestamp: Date.now() }));
      } catch (_) { /* localStorage full — ignore */ }
    } catch (err) {
      console.error('Agir data fetch error:', err);
    } finally {
      setLoading(false);
      loadedOnce.current = true;
    }
  }, [selectedBrand, actionsCacheKey]);

  const fetchMissions = useCallback(async () => {
    if (!selectedBrand) return;
    try {
      const res = await missionsAPI.getProgress(selectedBrand);
      const data = res.data || res;
      setMissions(data.missions || []);
    } catch (err) {
      console.warn('Missions fetch warning:', err);
    }
  }, [selectedBrand]);

  const fetchBrandImage = useCallback(async () => {
    if (!selectedBrand) return;
    try {
      const res = await monitoringAPI.getBrandImages(selectedBrand);
      const data = res.data || {};
      const urls = data.photo_urls || [];
      if (urls.length > 2) setBrandImageUrl(urls[2]);
      else if (urls.length > 0) setBrandImageUrl(urls[0]);
      else if (data.brand_photo_url) setBrandImageUrl(data.brand_photo_url);
      else {
        const images = data.images || [];
        if (images.length > 0) setBrandImageUrl(images[0].image_url || images[0].url || null);
      }
    } catch (err) { /* Silent fail */ }
  }, [selectedBrand]);

  // Expert RP (Tab 1) — with 24h localStorage cache
  const fetchExpertRP = useCallback(async (force = false) => {
    if (!selectedBrand) return;
    const cacheKey = `expert_rp_cache_${selectedBrand}`;
    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { data: cachedData, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DATA_CACHE_TTL) {
            setExpertRP(cachedData);
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }
    setExpertLoading(true);
    try {
      const res = await seoAPI.getExpertRP(selectedBrand);
      const data = res.data || null;
      setExpertRP(data);
      // Save to localStorage cache
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
      } catch (_) { /* localStorage full — ignore */ }
    } catch (err) {
      console.error('Expert RP fetch error:', err);
    } finally {
      setExpertLoading(false);
    }
  }, [selectedBrand]);

  // SEO/SERP (Tab 3) — with 24h localStorage cache
  const fetchSerpData = useCallback(async (force = false) => {
    if (!selectedBrand) return;
    const cacheKey = `serp_cache_${selectedBrand}`;
    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { data: cachedData, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DATA_CACHE_TTL) {
            setSerpResults(cachedData.serpResults);
            setCampaigns(cachedData.campaigns);
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }
    setSerpLoading(true);
    try {
      const [serpRes, campRes] = await Promise.all([
        agirAPI.getSerpResults(selectedBrand).catch(() => ({ data: null })),
        agirAPI.getCampaigns(selectedBrand).catch(() => ({ data: { campaigns: [] } })),
      ]);
      const serpData = serpRes.data || null;
      const campData = campRes.data?.campaigns || [];
      setSerpResults(serpData);
      setCampaigns(campData);
      // Save to localStorage cache
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: { serpResults: serpData, campaigns: campData },
          timestamp: Date.now(),
        }));
      } catch (_) { /* localStorage full — ignore */ }
    } catch (err) {
      console.error('SERP data fetch error:', err);
    } finally {
      setSerpLoading(false);
    }
  }, [selectedBrand]);

  // Recent mentions (Tab 4)
  const fetchRecentMentions = useCallback(async () => {
    if (!selectedBrand) return;
    setMentionsLoading(true);
    try {
      const res = await agirAPI.getRecentMentions(selectedBrand, { limit: 20 });
      setRecentMentions(res.data?.mentions || []);
    } catch (err) {
      console.error('Mentions fetch error:', err);
    } finally {
      setMentionsLoading(false);
    }
  }, [selectedBrand]);

  // Influence map (pre-fetched at mount with 24h localStorage cache)
  const fetchInfluence = useCallback(async () => {
    if (!selectedBrand) return;
    const cacheKey = `influence_data_${selectedBrand}`;
    // Check localStorage cache first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data: cachedData, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < DATA_CACHE_TTL) {
          setInfluencers(cachedData || []);
          return;
        }
      }
    } catch (_) { /* ignore parse errors */ }

    setInfluenceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const resp = await fetch(`${API}/api/influence/map/${selectedBrand}?days=90`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        const influencerList = data.influencers || [];
        setInfluencers(influencerList);
        // Save to localStorage cache
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ data: influencerList, timestamp: Date.now() }));
        } catch (_) { /* localStorage full */ }
      }
    } catch (e) { console.warn('[influence] fetch error', e); }
    setInfluenceLoading(false);
  }, [selectedBrand]);

  // Press contacts (Tab 5)
  const fetchPressContacts = useCallback(async (force = false) => {
    if (!selectedBrand) return;
    setPressLoading(true);
    try {
      const res = await agirAPI.getPressContacts(selectedBrand, force);
      setPressContacts(res.data?.contacts || []);
    } catch (err) {
      console.error('Press contacts fetch error:', err);
    } finally {
      setPressLoading(false);
    }
  }, [selectedBrand]);

  // Core data — single effect on brand change (cached fetches pre-loaded here)
  useEffect(() => {
    fetchData();
    fetchMissions();
    fetchBrandImage();
    fetchInfluence();
    fetchExpertRP();  // pre-fetch with 24h cache — instant on Tab 1/3
    fetchSerpData();  // pre-fetch with 24h cache — instant on Tab 3
  }, [selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when CRON freshness invalidates caches (only on actual change, not on mount)
  const prevCacheVersion = useRef(cacheVersion);
  useEffect(() => {
    if (cacheVersion !== prevCacheVersion.current) {
      prevCacheVersion.current = cacheVersion;
      fetchData(true);
    }
  }, [cacheVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab-dependent fetches — only for data NOT pre-fetched at mount
  useEffect(() => {
    if (activeTab === 4) fetchRecentMentions();
    else if (activeTab === 5) { fetchPressContacts(); }
  }, [activeTab, selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  // ========= HANDLERS =========

  const handleUpdateActionStatus = async (actionId, newStatus) => {
    try {
      await agirAPI.updateActionStatus(actionId, newStatus);
      setActions(prev => prev.map(a =>
        a.id === actionId ? { ...a, status: newStatus } : a
      ));
      setSuccess('Statut mis a jour');
    } catch (err) {
      setError('Erreur lors de la mise a jour');
    }
  };

  const handleDeleteAction = (actionId) => {
    setConfirmDialog({
      open: true,
      title: 'Supprimer',
      message: 'Etes-vous sur de vouloir supprimer cette action definitivement ?',
      onConfirm: async () => {
        try {
          await agirAPI.deleteActionItem(actionId);
          setActions(prev => prev.filter(a => a.id !== actionId));
          setSuccess('Action supprimee');
        } catch (err) {
          setError('Erreur lors de la suppression');
        }
      },
    });
  };


  const handleGenerateContent = async (data) => {
    setGeneratingContent(true);
    setGeneratedContent(null);
    setGeneratedVisual(null);
    setGeneratingStep('Recherche approfondie sur le sujet...');
    try {
      const res = await agirAPI.generateContent({
        brand_name: brandName,
        brand_id: selectedBrand,
        topic: data.topic,
        content_type: data.contentType,
        platform: data.platform,
        profile_type: profileType,
      });
      const raw = res.data?.content;
      let displayContent = null;
      if (typeof raw === 'object' && raw !== null) {
        const parts = [];
        if (raw.headline) parts.push(raw.headline);
        if (raw.content) parts.push(raw.content);
        if (raw.hashtags) {
          const tags = Array.isArray(raw.hashtags) ? raw.hashtags.join(' ') : raw.hashtags;
          parts.push(tags);
        }
        if (raw.call_to_action) parts.push(`\n${raw.call_to_action}`);
        if (raw.best_posting_time) parts.push(`\nMeilleur moment : ${raw.best_posting_time}`);
        if (raw.tips) {
          const tipsList = Array.isArray(raw.tips) ? raw.tips.join('\n- ') : raw.tips;
          parts.push(`\nConseils :\n- ${tipsList}`);
        }
        displayContent = parts.join('\n\n') || JSON.stringify(raw, null, 2);
      } else {
        displayContent = raw || null;
      }
      setGeneratedContent(displayContent);
      if (typeof raw === 'object' && raw?.visual) {
        setGeneratedVisual(raw.visual);
      }

      // Auto-record (non-blocking)
      if (displayContent && selectedBrand) {
        try {
          await agirAPI.recordAction(selectedBrand, {
            action_type: 'content_generation',
            platform: data.platform,
            title: `Contenu genere: ${data.topic}`,
            content_text: (typeof raw === 'object' && raw?.content)
              ? raw.content.substring(0, 500)
              : (displayContent || '').substring(0, 500),
          });
        } catch (recordErr) {
          console.warn('Auto-record content generation failed:', recordErr);
        }
      }
    } catch (err) {
      setError('Erreur lors de la generation de contenu');
    } finally {
      setGeneratingContent(false);
      setGeneratingStep('');
    }
  };

  const handleGoToContent = (topic, platform = '') => {
    const normalizedPlatform = normalizePlatform(platform);
    const contentType = detectContentType(topic, normalizedPlatform);
    setContentPrefill({ topic, platform: normalizedPlatform, contentType });
    setGeneratedContent(null);
    setGeneratedVisual(null);
    setActiveTab(1);
  };

  const handleCopyMessage = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Workflow status menu
  const handleOpenStatusMenu = (e, actionId) => {
    setStatusMenuAnchor(e.currentTarget);
    setStatusMenuActionId(actionId);
  };
  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuActionId(null);
  };
  const handleStatusMenuSelect = async (newStatus) => {
    if (statusMenuActionId) {
      await handleUpdateActionStatus(statusMenuActionId, newStatus);
    }
    handleCloseStatusMenu();
  };

  // SERP scan — also updates localStorage cache
  const handleScanSerp = async (force = false) => {
    if (!selectedBrand) return;
    setSerpLoading(true);
    try {
      const res = await seoAPI.scanSerp(selectedBrand, null, force);
      const serpData = res.data || null;
      setSerpResults(serpData);
      // Update localStorage cache with fresh scan results
      try {
        const cacheKey = `serp_cache_${selectedBrand}`;
        const existing = localStorage.getItem(cacheKey);
        const campData = existing ? JSON.parse(existing).data?.campaigns || [] : campaigns;
        localStorage.setItem(cacheKey, JSON.stringify({
          data: { serpResults: serpData, campaigns: campData },
          timestamp: Date.now(),
        }));
      } catch (_) { /* localStorage full — ignore */ }
      setSuccess('Scan SERP termine');
    } catch (err) {
      setError('Erreur lors du scan SERP');
    } finally {
      setSerpLoading(false);
    }
  };

  // Generate reply for a mention
  const handleGenerateReply = async (mention) => {
    setReplyGenerating(mention.id);
    try {
      const res = await agirAPI.generateContent({
        brand_name: brandName,
        brand_id: selectedBrand,
        topic: `Reponse a une mention ${mention.sentiment} sur ${mention.platform}: "${(mention.text || '').substring(0, 200)}"`,
        content_type: 'reponse_commentaire',
        platform: mention.platform || 'web',
        profile_type: profileType,
      });
      const raw = res.data?.content;
      const replyText = typeof raw === 'object' ? (raw.content || raw.message || JSON.stringify(raw)) : (raw || '');
      setGeneratedReplies(prev => ({ ...prev, [mention.id]: replyText }));
    } catch (err) {
      setError('Erreur lors de la generation de reponse');
    } finally {
      setReplyGenerating(null);
    }
  };

  // Generate delete template
  const handleGenerateTemplate = async (templateId) => {
    setGeneratingTemplate(templateId);
    try {
      const template = DELETE_TEMPLATES.find(t => t.id === templateId);
      const res = await agirAPI.generateContent({
        brand_name: brandName,
        brand_id: selectedBrand,
        topic: `Template ${template.title} pour ${brandName}. Generer un modele de lettre/formulaire pour ${template.description}`,
        content_type: 'communique',
        platform: 'web',
        profile_type: profileType,
      });
      const raw = res.data?.content;
      const text = typeof raw === 'object' ? (raw.content || JSON.stringify(raw)) : (raw || '');
      setGeneratedTemplates(prev => ({ ...prev, [templateId]: text }));
    } catch (err) {
      setError('Erreur lors de la generation du template');
    } finally {
      setGeneratingTemplate(null);
    }
  };

  // Press contact CRUD
  const handleOpenPressDialog = (contact = null) => {
    if (contact) {
      setPressEditId(contact.id);
      setPressForm({
        name: contact.name || '',
        media: contact.media || '',
        email: contact.email || '',
        phone: contact.phone || '',
        notes: contact.notes || '',
      });
    } else {
      setPressEditId(null);
      setPressForm({ name: '', media: '', email: '', phone: '', notes: '' });
    }
    setPressDialogOpen(true);
  };

  const handleSavePressContact = async () => {
    if (!selectedBrand || !pressForm.name.trim()) return;
    try {
      if (pressEditId) {
        await agirAPI.updatePressContact(selectedBrand, pressEditId, pressForm);
        setSuccess('Contact mis a jour');
      } else {
        await agirAPI.createPressContact(selectedBrand, pressForm);
        setSuccess('Contact ajoute');
      }
      setPressDialogOpen(false);
      fetchPressContacts();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du contact');
    }
  };

  const handleDeletePressContact = (contactId) => {
    setConfirmDialog({
      open: true,
      title: 'Supprimer',
      message: 'Etes-vous sur de vouloir supprimer ce contact ?',
      onConfirm: async () => {
        try {
          await agirAPI.deletePressContact(selectedBrand, contactId);
          setPressContacts(prev => prev.filter(c => c.id !== contactId));
          setSuccess('Contact supprime');
        } catch (err) {
          setError('Erreur lors de la suppression');
        }
      },
    });
  };

  // ========= EARLY RETURNS =========

  if (!selectedBrand) {
    return (
      <Box sx={{ bgcolor: colors.ivoryWarm, mx: -3, mt: -3, px: 3, pt: 3, pb: 4, minHeight: '80vh' }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: colors.textMed, fontFamily: '"DM Sans", sans-serif' }}>
            Selectionnez une marque pour agir
          </Typography>
        </Box>
      </Box>
    );
  }

  // ========= HELPERS =========

  const priorityColor = (p) => {
    if (p === 'urgent') return colors.priorityUrgent;
    if (p === 'high') return colors.priorityHigh;
    if (p === 'medium') return colors.primary;
    return colors.textMed;
  };

  const priorityLabel = (p) => {
    if (p === 'urgent') return 'Urgent';
    if (p === 'high') return 'Haute';
    if (p === 'medium') return 'Moyenne';
    return 'Basse';
  };

  // Apply filters before grouping
  const filteredActions = actions.filter(a => {
    if (filterStatus === 'active' && (a.status === 'completed' || a.status === 'skipped')) return false;
    if (filterStatus === 'completed' && a.status !== 'completed') return false;
    if (filterStatus === 'skipped' && a.status !== 'skipped') return false;
    if (filterPriority !== 'all' && a.priority !== filterPriority) return false;
    return true;
  });

  // Group filtered actions by mission_key using fetched missions as source of truth
  const actionsByMission = (() => {
    if (!filteredActions.length) return null;
    const groups = {};
    missions.forEach(m => {
      const missionActions = filteredActions.filter(a => a.mission_key === m.mission_key);
      if (missionActions.length > 0) {
        groups[m.mission_key] = { mission: m, actions: missionActions };
      }
    });
    const knownKeys = new Set(missions.map(m => m.mission_key));
    const ungrouped = filteredActions.filter(a => !a.mission_key || !knownKeys.has(a.mission_key));
    if (ungrouped.length > 0) {
      groups['_other'] = { mission: { name: 'Autres actions', mission_key: '_other', icon: 'shield', score: 0 }, actions: ungrouped };
    }
    return Object.keys(groups).length > 0 ? groups : null;
  })();

  const sentimentColor = (s) => {
    if (s === 'positive') return colors.success;
    if (s === 'negative') return colors.danger;
    return colors.gray400;
  };

  // ========= RENDER ACTION ACCORDION =========
  const renderAction = (action, index) => {
    const wfStatus = action.status || 'pending';
    const wfConfig = WORKFLOW_STATUS_CONFIG[wfStatus] || WORKFLOW_STATUS_CONFIG.pending;
    const isInactive = action.status === 'completed' || action.status === 'skipped';

    // Dedup: don't show description if identical to title
    const showDescription = action.description
      && action.description !== action.action
      && action.description !== action.title;

    // Platform/action_type for Row 3 inline tags
    const channelText = (action.suggested_response?.channel) || action.platform || action.canal || action.channel || '';
    const actionTypeLabel = ACTION_TYPE_LABELS[action.action_type]?.label || '';

    return (
      <ScrollReveal key={action.id} delay={index * 0.08}>
        <GlassCard
          hover
          glow={false}
          delay={0}
          sx={{
            mb: 1.5,
            borderLeft: `3px solid ${priorityColor(action.priority)}`,
            borderRadius: '16px !important',
            opacity: isInactive ? 0.55 : 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          <Accordion
            elevation={0}
            disableGutters
            sx={{
              bgcolor: 'transparent',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { m: 0 },
            }}
          >
            {/* === REDESIGNED HEADER (3 rows) === */}
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: colors.textMed }} />}
              sx={{ px: 2.5, py: 1, minHeight: 64, '& .MuiAccordionSummary-content': { my: 0.5, flexDirection: 'column', gap: 0.5 } }}
            >
              {/* Row 1: Priority chip (left) + Status chip clickable (right) */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Chip
                  label={priorityLabel(action.priority)}
                  size="small"
                  sx={{
                    fontSize: '0.62rem', height: 18, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    bgcolor: `${priorityColor(action.priority)}15`,
                    color: priorityColor(action.priority),
                    borderRadius: '100px',
                    fontFamily: '"DM Sans", sans-serif',
                  }}
                />
                <Chip
                  label={wfConfig.label}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleOpenStatusMenu(e, action.id); }}
                  sx={{
                    fontSize: '0.68rem', height: 22,
                    bgcolor: wfConfig.bg, color: wfConfig.color,
                    borderRadius: '100px', cursor: 'pointer',
                    fontFamily: '"DM Sans", sans-serif', fontWeight: 600,
                    '&:hover': { opacity: 0.8 },
                  }}
                />
              </Box>
              {/* Row 2: Title (Playfair, 2 lines max) */}
              <Typography sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: '1rem', fontWeight: 500,
                color: colors.charcoal, lineHeight: 1.35,
                display: '-webkit-box', WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical', overflow: 'hidden',
                width: '100%',
              }}>
                {action.action || action.title || 'Action'}
              </Typography>
              {/* Row 3: Platform + action_type as inline text (discreet) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {(channelText || actionTypeLabel) && (
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '0.72rem', color: colors.textLight,
                    fontWeight: 400,
                  }}>
                    {[channelText, actionTypeLabel].filter(Boolean).join(' · ')}
                  </Typography>
                )}
                {action.objective_id && (
                  <Chip
                    label="Lie a un objectif"
                    size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontFamily: '"DM Sans", sans-serif', bgcolor: colors.primaryBg, color: colors.primary }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
              <Divider sx={{ mb: 1.5, borderColor: colors.borderLight }} />

              {(() => {
                const sr = action.suggested_response || {};
                const msgText = (typeof sr === 'object' ? sr.message : null) || action.message || action.suggested_message || '';
                const audienceText = (typeof sr === 'object' ? sr.audience : null) || action.audience || '';
                const imp = action.impact || {};
                const impactParts = typeof imp === 'object' && imp !== null
                  ? [imp.predicted, imp.nss_change != null ? `NSS ${imp.nss_change > 0 ? '+' : ''}${imp.nss_change}` : null, imp.risk_if_ignored].filter(Boolean)
                  : [];
                const impactText = impactParts.length > 0 ? impactParts.join(' — ') : (typeof imp === 'string' ? imp : '');
                const aiObj = action.ai || {};
                const reasoning = (typeof aiObj === 'object' ? aiObj.reasoning : null) || action.reasoning || action.raisonnement || '';

                return (
                  <>
                    {showDescription && (
                      <Typography variant="body2" sx={{ color: colors.textMed, mb: 1.5, lineHeight: 1.6, wordBreak: 'break-word', fontFamily: '"DM Sans", sans-serif', textAlign: 'justify' }}>
                        {action.description}
                      </Typography>
                    )}
                    {msgText && (
                      <Box sx={{ mb: 1.5, p: 1.5, bgcolor: colors.ivory, borderRadius: '12px', border: `1px solid ${colors.borderLight}`, position: 'relative' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: colors.textLight, display: 'block', mb: 0.5, fontFamily: '"DM Sans", sans-serif' }}>Message suggere</Typography>
                        <Typography variant="body2" sx={{ color: colors.charcoal, lineHeight: 1.6, pr: 4, fontStyle: 'italic', wordBreak: 'break-word', fontFamily: '"DM Sans", sans-serif', textAlign: 'justify' }}>{msgText}</Typography>
                        <Tooltip title={copiedId === action.id ? 'Copie !' : 'Copier'}>
                          <IconButton aria-label="Copier le message" size="small" onClick={() => handleCopyMessage(msgText, action.id)} sx={{ position: 'absolute', top: 8, right: 8, color: copiedId === action.id ? colors.success : colors.textLight }}>
                            {copiedId === action.id ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    {audienceText && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }}>Audience:</Typography>
                        <Typography variant="body2" sx={{ color: colors.textMed, fontFamily: '"DM Sans", sans-serif' }}>{audienceText}</Typography>
                      </Box>
                    )}
                    {impactText && (
                      <Box sx={{ mb: 1, p: 1, bgcolor: colors.primaryBg, borderRadius: '8px', border: `1px solid ${colors.primary}20` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                          <TrendingUpIcon sx={{ fontSize: 14, color: colors.primary }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: colors.primary, fontFamily: '"DM Sans", sans-serif' }}>Impact predit</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: colors.textMed, fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif', textAlign: 'justify' }}>{impactText}</Typography>
                      </Box>
                    )}
                    {reasoning && (
                      <Typography variant="body2" sx={{ color: colors.textLight, fontStyle: 'italic', fontSize: '0.8rem', mb: 1.5, fontFamily: '"DM Sans", sans-serif', textAlign: 'justify' }}>{reasoning}</Typography>
                    )}
                  </>
                );
              })()}

              {/* === REDESIGNED BUTTONS — 2 tiers === */}
              {/* Tier 1: Primary actions */}
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {action.status !== 'completed' && action.status !== 'skipped' && (
                  <Button size="small" variant="contained" startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleUpdateActionStatus(action.id, 'completed')}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', borderRadius: '100px', px: 2.5, py: 0.6, bgcolor: colors.primary, transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { bgcolor: colors.primaryDark, transform: 'translateY(-1px)', boxShadow: '0 4px 16px rgba(27,94,64,0.2)' }, fontFamily: '"DM Sans", sans-serif' }}
                  >Marquer comme fait</Button>
                )}
                {action.status === 'skipped' ? (
                  <Button size="small" variant="outlined" startIcon={<RestoreIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleUpdateActionStatus(action.id, 'pending')}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', borderRadius: '100px', px: 2, borderColor: colors.primary, color: colors.primary, transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { borderColor: colors.primaryDark, bgcolor: colors.primaryBg }, fontFamily: '"DM Sans", sans-serif' }}
                  >Restaurer</Button>
                ) : (
                  <Button size="small" variant="outlined"
                    onClick={() => {
                      const atInfo = ACTION_TYPE_LABELS[action.action_type];
                      const targetTab = atInfo?.tab ?? 1;
                      if (targetTab === 1) {
                        handleGoToContent(action.action || action.title || '', channelText);
                      } else {
                        setActiveTab(targetTab);
                      }
                      if (action.status !== 'completed') handleUpdateActionStatus(action.id, 'in_progress');
                    }}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', borderRadius: '100px', px: 2, borderColor: colors.primary, color: colors.primary, transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { borderColor: colors.primaryDark, bgcolor: colors.primaryBg }, fontFamily: '"DM Sans", sans-serif' }}
                  >{(ACTION_TYPE_LABELS[action.action_type]?.label) || 'Generer contenu'}</Button>
                )}
              </Box>
              {/* Divider between tiers */}
              <Divider sx={{ my: 1.5, borderColor: 'rgba(221,213,200,0.3)' }} />
              {/* Tier 2: Secondary actions (right-aligned) */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
                {action.status !== 'completed' && action.status !== 'skipped' && (
                  <Button size="small" variant="text"
                    onClick={() => handleUpdateActionStatus(action.id, 'skipped')}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', color: colors.textLight, fontFamily: '"DM Sans", sans-serif', '&:hover': { textDecoration: 'underline', textUnderlineOffset: '3px', bgcolor: 'transparent', color: colors.textMed } }}
                  >Ignorer</Button>
                )}
                <Button size="small" variant="text"
                  onClick={() => handleDeleteAction(action.id)}
                  sx={{ textTransform: 'none', fontSize: '0.72rem', color: colors.danger, fontFamily: '"DM Sans", sans-serif', '&:hover': { textDecoration: 'underline', textUnderlineOffset: '3px', bgcolor: 'transparent' } }}
                >Supprimer</Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </GlassCard>
      </ScrollReveal>
    );
  };

  // ========= TAB DEFINITIONS =========
  const tabs = [
    { label: `PLAN D'ACTION${actions.length ? ` (${actions.filter(a => a.status !== 'completed' && a.status !== 'skipped').length})` : ''}`, idx: 0 },
    { label: 'CREER DU CONTENU', idx: 1 },
    { label: 'EFFACER DU CONTENU', idx: 2 },
    { label: 'GERER SEO/SERP', idx: 3 },
    { label: 'REPONDRE AUX MENTIONS', idx: 4 },
    { label: 'RELATIONS', idx: 5 },
  ];

  return (
    <Box sx={{ bgcolor: colors.ivoryWarm, mx: { xs: -2, sm: -3, md: -7.5 }, mt: -3, px: { xs: 2, sm: 4, md: 5 }, pt: 3, pb: 4, minHeight: '80vh', overflowX: 'hidden' }}>
      {/* Header */}
      <ScrollReveal>
        <Box sx={{ pt: 5, pb: 4, mb: 4 }}>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: { xs: '3.2rem', sm: '4.8rem' },
              color: colors.charcoal,
              lineHeight: 1.1,
              mb: 1,
              textWrap: 'balance',
            }}
          >
            Votre plan{' '}
            <Box component="em" sx={{ color: colors.primary, fontStyle: 'italic' }}>
              d'action
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '1rem',
              color: colors.textMed,
              fontWeight: 400,
            }}
          >
            Actions, contenu, SEO, mentions & relations — {brandName}
          </Typography>
          <LastScanLabel timestamp={lastUpdated} />
        </Box>
      </ScrollReveal>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Tabs — editorial luxe — scrollable */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mb: 3, overflowX: 'auto', pb: 1 }}>
        {tabs.map((tab, i) => (
          <React.Fragment key={tab.idx}>
            {i > 0 && (
              <Typography sx={{ color: colors.textLight, mx: { xs: 1.5, sm: 2.5 }, fontSize: '0.8rem', userSelect: 'none', flexShrink: 0 }}>
                ·
              </Typography>
            )}
            <Box
              onClick={() => setActiveTab(tab.idx)}
              sx={{
                cursor: 'pointer',
                pb: 1,
                flexShrink: 0,
                borderBottom: activeTab === tab.idx ? `2px solid ${colors.primary}` : '2px solid transparent',
                transition: 'border-color 0.2s ease',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: { xs: '0.68rem', sm: '0.78rem' },
                  fontWeight: activeTab === tab.idx ? 600 : 400,
                  letterSpacing: '0.12em',
                  color: activeTab === tab.idx ? colors.charcoal : colors.textLight,
                  transition: 'color 0.2s ease',
                  whiteSpace: 'nowrap',
                  '&:hover': { color: colors.charcoal },
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Box>

      {/* ==========================================
          TAB 0: PLAN D'ACTION
          ========================================== */}
      <TabPanel value={activeTab} index={0}>
        {actions.length > 0 && !loading && (
          <Box sx={{ display: 'flex', gap: 0.75, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textLight, fontFamily: '"DM Sans", sans-serif', mr: 0.5 }}>Statut</Typography>
            {[
              { key: 'active', label: 'Actives' },
              { key: 'all', label: 'Toutes' },
              { key: 'completed', label: 'Terminees' },
              { key: 'skipped', label: 'Ignorees' },
            ].map(f => (
              <Chip key={f.key} label={f.label} size="small" variant="outlined"
                onClick={() => setFilterStatus(f.key)}
                sx={{
                  fontWeight: filterStatus === f.key ? 600 : 400, fontSize: '0.68rem', height: 26, borderRadius: '100px', fontFamily: '"DM Sans", sans-serif',
                  bgcolor: filterStatus === f.key ? colors.primary : 'transparent',
                  color: filterStatus === f.key ? 'white' : colors.textMed,
                  borderColor: filterStatus === f.key ? colors.primary : colors.border,
                  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': { bgcolor: filterStatus === f.key ? colors.primaryDark : 'rgba(27,94,64,0.04)', borderColor: filterStatus === f.key ? colors.primaryDark : colors.textLight },
                }}
              />
            ))}
            <Box sx={{ width: '1px', height: 20, bgcolor: colors.border, mx: 1.5 }} />
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textLight, fontFamily: '"DM Sans", sans-serif', mr: 0.5 }}>Priorite</Typography>
            {[
              { key: 'all', label: 'Toutes' },
              { key: 'urgent', label: 'Urgent' },
              { key: 'high', label: 'Haute' },
              { key: 'medium', label: 'Moyenne' },
              { key: 'low', label: 'Basse' },
            ].map(f => (
              <Chip key={f.key} label={f.label} size="small" variant="outlined"
                onClick={() => setFilterPriority(f.key)}
                sx={{
                  fontWeight: filterPriority === f.key ? 600 : 400, fontSize: '0.68rem', height: 26, borderRadius: '100px', fontFamily: '"DM Sans", sans-serif',
                  bgcolor: filterPriority === f.key ? colors.primary : 'transparent',
                  color: filterPriority === f.key ? 'white' : colors.textMed,
                  borderColor: filterPriority === f.key ? colors.primary : colors.border,
                  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': { bgcolor: filterPriority === f.key ? colors.primaryDark : 'rgba(27,94,64,0.04)', borderColor: filterPriority === f.key ? colors.primaryDark : colors.textLight },
                }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 3, sm: 4, md: 5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loading ? (
              <>{[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: '16px', bgcolor: colors.borderLight }} />)}</>
            ) : actions.length === 0 ? (
              <GlassCard glow={false}>
                <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                  <Box sx={{ width: 88, height: 88, borderRadius: '50%', bgcolor: colors.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <FlagIcon sx={{ fontSize: 40, color: colors.primary }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: colors.textMed, fontFamily: "'DM Sans', sans-serif", mb: 1, textWrap: 'balance' }}>Aucune action recommandee</Typography>
                  <Typography variant="body2" sx={{ color: colors.textLight, fontFamily: "'DM Sans', sans-serif", textWrap: 'pretty', textAlign: 'justify' }}>Rendez-vous dans <strong>Comprendre</strong> pour analyser les mentions et appliquer des actions recommandees.</Typography>
                </Box>
              </GlassCard>
            ) : filteredActions.length === 0 ? (
              <GlassCard glow={false}>
                <Box sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Typography variant="body2" sx={{ color: colors.textMed, fontFamily: '"DM Sans", sans-serif' }}>Aucune action ne correspond aux filtres selectionnes.</Typography>
                  <Button size="small" onClick={() => { setFilterStatus('all'); setFilterPriority('all'); }}
                    sx={{ mt: 1, textTransform: 'none', color: colors.primary, fontFamily: '"DM Sans", sans-serif' }}
                  >Reinitialiser les filtres</Button>
                </Box>
              </GlassCard>
            ) : actionsByMission ? (
              <>
                {Object.entries(actionsByMission).map(([mKey, group]) => {
                  const score = group.mission.score || 0;
                  const borderColor = mKey === '_other' ? colors.gray400 : score >= 70 ? colors.success : score >= 40 ? colors.warning : colors.danger;
                  const urgentCount = group.actions.filter(a => a.priority === 'urgent' || a.priority === 'high').length;
                  const inProgressCount = group.actions.filter(a => a.status === 'in_progress').length;
                  const completedCount = group.actions.filter(a => a.status === 'completed').length;
                  const parts = [`${group.actions.length} action${group.actions.length > 1 ? 's' : ''}`];
                  if (urgentCount > 0) parts.push(`${urgentCount} urgente${urgentCount > 1 ? 's' : ''}`);
                  if (inProgressCount > 0) parts.push(`${inProgressCount} en cours`);
                  if (completedCount > 0) parts.push(`${completedCount} terminee${completedCount > 1 ? 's' : ''}`);
                  const summaryText = parts.join(' — ');
                  return (
                    <CollapsibleSection key={mKey} title={MISSION_LABELS[mKey] || group.mission.name} summary={summaryText}
                      borderColor={borderColor} info={MISSION_INFO[mKey] || null}
                      isOpen={openSection === mKey} onToggle={() => setOpenSection(prev => prev === mKey ? null : mKey)}
                      sx={{ mb: 0 }}
                    >
                      {group.actions.map((action, i) => renderAction(action, i))}
                    </CollapsibleSection>
                  );
                })}
              </>
            ) : (
              <>{filteredActions.map((action, i) => renderAction(action, i))}</>
            )}
          </Box>

          {/* RIGHT — Photo */}
          <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'relative', height: '100%' }}>
            <Box sx={{ borderRadius: '20px', overflow: 'hidden', position: 'relative', minHeight: 380, height: '100%' }}>
              <Box sx={{ position: 'absolute', inset: 0, background: brandImageUrl ? `url(${brandImageUrl}) center 20%/cover` : 'url(https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80) center 20%/cover' }} />
              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 50%, rgba(26,26,24,0.7) 100%)' }} />
              <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 3, zIndex: 2 }}>
                <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontStyle: 'italic', fontSize: '0.95rem', color: 'rgba(255,255,255,0.95)', lineHeight: 1.5, mb: 0.5 }}>
                  "{getDailyCitation('agir', selectedBrand).text}"
                </Typography>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>— {getDailyCitation('agir', selectedBrand).author}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </TabPanel>

      {/* ==========================================
          TAB 1: CREER DU CONTENU
          ========================================== */}
      <TabPanel value={activeTab} index={1}>
        <Box>
          {expertLoading ? (
            <Skeleton variant="rounded" height={200} sx={{ mb: 3, borderRadius: '16px' }} />
          ) : expertRP ? (
            <GlassCard glow sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AutoAwesomeIcon sx={{ color: colors.primary, fontSize: 22 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.charcoal, fontFamily: '"Playfair Display", Georgia, serif' }}>Recommandations Expert RP</Typography>
                </Box>
                {expertRP.objectives_summary && (
                  <Typography variant="body2" sx={{ color: colors.textMed, mb: 2, lineHeight: 1.7, wordBreak: 'break-word', fontFamily: '"DM Sans", sans-serif', textAlign: 'justify' }}>{expertRP.objectives_summary}</Typography>
                )}
                {expertRP.recommendations?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: colors.charcoal, fontFamily: '"DM Sans", sans-serif' }}>Recommandations strategiques</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {expertRP.recommendations.slice(0, 3).map((rec, i) => (
                        <Box component="li" key={i} sx={{ mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: colors.textMed, fontFamily: '"DM Sans", sans-serif' }}>{typeof rec === 'string' ? rec : rec.text || rec.recommendation}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {expertRP.content_suggestions?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: colors.charcoal, fontFamily: '"DM Sans", sans-serif' }}>Suggestions de contenu</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {expertRP.content_suggestions.slice(0, 3).map((sug, i) => {
                        const sugText = typeof sug === 'string' ? sug : sug.title || sug.topic;
                        const sugPlatform = typeof sug === 'object' ? sug.platform || '' : '';
                        return (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip label={sugText} size="small"
                              onClick={() => {
                                const np = normalizePlatform(sugPlatform);
                                setContentPrefill({ topic: sugText, platform: np, contentType: detectContentType(sugText, np) });
                                setGeneratedContent(null);
                                setGeneratedVisual(null);
                                setTimeout(() => document.getElementById('content-generator')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                              }}
                              sx={{ cursor: 'pointer', bgcolor: colors.primaryBg, color: colors.primary, fontWeight: 500, borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', '&:hover': { bgcolor: `${colors.primary}25` } }}
                              deleteIcon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                              onDelete={() => {
                                const np = normalizePlatform(sugPlatform);
                                setContentPrefill({ topic: sugText, platform: np, contentType: detectContentType(sugText, np) });
                                setGeneratedContent(null);
                                setGeneratedVisual(null);
                                setTimeout(() => document.getElementById('content-generator')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                              }}
                            />
                            <Chip label="Rayonner" size="small" sx={{ borderRadius: '9999px', fontSize: '0.65rem', fontWeight: 600, bgcolor: 'rgba(27,94,64,0.08)', color: colors.primary, fontFamily: '"DM Sans", sans-serif' }} />
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {expertRP.seo_suggestions?.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: colors.charcoal, fontFamily: '"DM Sans", sans-serif' }}>Campagnes SEO suggerees</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {expertRP.seo_suggestions.map((sug, i) => (
                        <Chip key={i} label={typeof sug === 'string' ? sug : sug.keyword || sug.name} size="small" variant="outlined"
                          sx={{ borderColor: colors.borderLight, color: colors.textMed, borderRadius: '100px', fontFamily: '"DM Sans", sans-serif' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </GlassCard>
          ) : null}

          <GlassCard glow={false} hover={false}>
            <Box sx={{ p: 3 }}>
              <div id="content-generator">
                <ContentGenerator
                  brandName={brandName} profileType={profileType}
                  onGenerate={handleGenerateContent} loading={generatingContent}
                  generatedContent={generatedContent}
                  generatedVisual={generatedVisual}
                  initialTopic={contentPrefill.topic} initialPlatform={contentPrefill.platform}
                  initialContentType={contentPrefill.contentType} generatingStep={generatingStep}
                />
              </div>
            </Box>
          </GlassCard>
        </Box>
      </TabPanel>

      {/* ==========================================
          TAB 2: EFFACER DU CONTENU
          ========================================== */}
      <TabPanel value={activeTab} index={2}>
        <Box>
          <Typography variant="body2" sx={{ color: colors.textMed, mb: 3, fontFamily: '"DM Sans", sans-serif', maxWidth: 600, lineHeight: 1.7 }}>
            Generez des templates pour demander la suppression de contenu nuisible. Chaque modele est adapte au contexte de {brandName || 'votre marque'}.
          </Typography>

          <Grid container spacing={3}>
            {DELETE_TEMPLATES.map((tmpl) => (
              <Grid item xs={12} sm={6} key={tmpl.id}>
                <GlassCard glow={false} hover sx={{ height: '100%' }}>
                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box sx={{ color: colors.primary, display: 'flex' }}>{tmpl.icon}</Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.charcoal, fontFamily: '"DM Sans", sans-serif' }}>{tmpl.title}</Typography>
                        <Chip label={tmpl.type} size="small" sx={{ fontSize: '0.68rem', height: 18, bgcolor: colors.primaryBg, color: colors.primary, borderRadius: '100px', mt: 0.3 }} />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.textMed, lineHeight: 1.6, mb: 2, flex: 1, fontFamily: '"DM Sans", sans-serif' }}>{tmpl.description}</Typography>

                    {generatedTemplates[tmpl.id] ? (
                      <Box sx={{ mb: 2, p: 2, bgcolor: colors.ivory, borderRadius: '12px', border: `1px solid ${colors.borderLight}`, position: 'relative', maxHeight: 300, overflow: 'auto' }}>
                        <Typography variant="body2" sx={{ color: colors.charcoal, whiteSpace: 'pre-wrap', fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif', pr: 3 }}>
                          {generatedTemplates[tmpl.id]}
                        </Typography>
                        <Tooltip title={copiedId === tmpl.id ? 'Copie !' : 'Copier'}>
                          <IconButton aria-label="Copier le template" size="small" onClick={() => handleCopyMessage(generatedTemplates[tmpl.id], tmpl.id)}
                            sx={{ position: 'absolute', top: 8, right: 8, color: copiedId === tmpl.id ? colors.success : colors.textLight }}
                          >
                            {copiedId === tmpl.id ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : null}

                    <Button variant="outlined" size="small"
                      onClick={() => handleGenerateTemplate(tmpl.id)}
                      disabled={generatingTemplate === tmpl.id}
                      startIcon={generatingTemplate === tmpl.id ? <CircularProgress size={14} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                      sx={{ textTransform: 'none', borderRadius: '100px', borderColor: colors.primary, color: colors.primary, fontFamily: '"DM Sans", sans-serif', alignSelf: 'flex-start' }}
                    >
                      {generatingTemplate === tmpl.id ? 'Generation...' : generatedTemplates[tmpl.id] ? 'Regenerer le template' : 'Generer le template'}
                    </Button>
                  </Box>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      </TabPanel>

      {/* ==========================================
          TAB 3: GERER SEO/SERP
          ========================================== */}
      <TabPanel value={activeTab} index={3}>
        <Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: colors.textMed, fontFamily: '"DM Sans", sans-serif' }}>
              Votre presence dans les resultats de recherche Google, actualisee automatiquement.
            </Typography>
          </Box>

          {/* SERP Score */}
          {serpResults && (
            <GlassCard glow sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: colors.textLight, letterSpacing: '0.1em', fontFamily: '"DM Sans", sans-serif' }}>SCORE SERP</Typography>
                    <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '2.5rem', fontWeight: 400, color: serpResults.serp_score >= 70 ? colors.success : serpResults.serp_score >= 40 ? colors.warning : colors.danger }}>
                      {serpResults.serp_score}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: colors.textMed, fontFamily: '"DM Sans", sans-serif' }}>
                      {serpResults.top_10_positive}/{serpResults.top_10_total} resultats favorables dans le top 10
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }}>
                      {serpResults.total_results} resultats totaux
                    </Typography>
                  </Box>
                </Box>

                {/* SERP results table */}
                {serpResults.results?.length > 0 && (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', bgcolor: colors.ivory }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', bgcolor: colors.ivory }}>Titre</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', bgcolor: colors.ivory }}>Domaine</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', bgcolor: colors.ivory }}>Sentiment</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serpResults.results.slice(0, 20).map((r, i) => (
                          <TableRow key={i} sx={{ '& td': { borderColor: colors.borderLight } }}>
                            <TableCell sx={{ fontSize: '0.75rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 600 }}>{r.position}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: '"DM Sans", sans-serif', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline', textDecorationColor: `${colors.primary}40`, textUnderlineOffset: '2px' }}>{r.title}</a> : r.title}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.7rem', color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }}>{r.domain}</TableCell>
                            <TableCell>
                              <Chip label={r.sentiment || 'neutral'} size="small"
                                sx={{ fontSize: '0.68rem', height: 18, borderRadius: '100px', bgcolor: sentimentColor(r.sentiment) + '15', color: sentimentColor(r.sentiment) }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </GlassCard>
          )}

          {/* Campaigns */}
          {campaigns.length > 0 && (
            <GlassCard glow={false}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Playfair Display", Georgia, serif' }}>Campagnes SEO</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif' }}>Nom</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif' }}>Mot-cle</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif' }}>Statut</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaigns.map((c, i) => (
                        <TableRow key={i} sx={{ '& td': { borderColor: colors.borderLight } }}>
                          <TableCell sx={{ fontSize: '0.75rem', fontFamily: '"DM Sans", sans-serif' }}>{c.name}</TableCell>
                          <TableCell>
                            <Chip label={c.campaign_type === 'boost' ? 'Boost' : 'Suppress'} size="small"
                              sx={{ fontSize: '0.68rem', height: 18, borderRadius: '100px', bgcolor: c.campaign_type === 'boost' ? colors.successBg : colors.dangerBg, color: c.campaign_type === 'boost' ? colors.success : colors.danger }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontFamily: '"DM Sans", sans-serif' }}>{c.target_keyword}</TableCell>
                          <TableCell>
                            <Chip label={c.status} size="small" sx={{ fontSize: '0.68rem', height: 18, borderRadius: '100px', bgcolor: c.status === 'active' ? colors.infoBg : colors.emeraldGlass, color: c.status === 'active' ? colors.info : colors.textMed }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </GlassCard>
          )}

          {/* Expert RP — SEO action suggestions */}
          {expertRP?.actions?.filter(a => a.action_type === 'seo_serp' || a.action_type === 'effacer_contenu').length > 0 && (
            <GlassCard glow sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AutoAwesomeIcon sx={{ color: colors.primary, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: '"Playfair Display", Georgia, serif', color: colors.charcoal }}>Actions SEO recommandees</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {expertRP.actions.filter(a => a.action_type === 'seo_serp' || a.action_type === 'effacer_contenu').map((action, i) => (
                    <Box key={i} sx={{ p: 2, bgcolor: colors.ivory, borderRadius: '12px', border: `1px solid ${colors.borderLight}`, borderLeft: `3px solid ${action.action_type === 'effacer_contenu' ? colors.priorityUrgent : colors.primary}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label={action.action_type === 'seo_serp' ? 'SEO' : 'Effacer'} size="small"
                          sx={{ fontSize: '0.65rem', height: 18, borderRadius: '100px', fontWeight: 600, fontFamily: '"DM Sans", sans-serif',
                            bgcolor: action.action_type === 'seo_serp' ? colors.primaryBg : `${colors.priorityUrgent}14`,
                            color: action.action_type === 'seo_serp' ? colors.primary : colors.priorityUrgent }} />
                        {action.mission_key && (
                          <Chip label={MISSION_LABELS[action.mission_key] || action.mission_key} size="small"
                            sx={{ fontSize: '0.65rem', height: 18, borderRadius: '100px', bgcolor: 'rgba(27,94,64,0.06)', color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }} />
                        )}
                        {action.delai && (
                          <Typography variant="caption" sx={{ color: colors.textLight, fontFamily: '"DM Sans", sans-serif', ml: 'auto' }}>{action.delai}</Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: colors.charcoal, fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem', lineHeight: 1.6, textAlign: 'justify' }}>
                        {action.action}
                      </Typography>
                      {action.impact_predit && (
                        <Typography variant="caption" sx={{ color: colors.primary, fontFamily: '"DM Sans", sans-serif', mt: 0.5, display: 'block', fontWeight: 500 }}>
                          Impact: {action.impact_predit}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </GlassCard>
          )}

          {!serpResults && !serpLoading && (
            <GlassCard glow={false}>
              <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                <Box sx={{ width: 88, height: 88, borderRadius: '50%', bgcolor: colors.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                  <SearchIcon sx={{ fontSize: 40, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ color: colors.textMed, mb: 1, fontFamily: '"DM Sans", sans-serif', textWrap: 'balance' }}>Aucun scan SERP</Typography>
                <Typography variant="body2" sx={{ color: colors.textLight, mb: 3, fontFamily: '"DM Sans", sans-serif' }}>Les resultats SERP apparaitront ici apres la prochaine collecte automatique.</Typography>
                <Button variant="contained" onClick={() => handleScanSerp(false)} disabled={serpLoading}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', borderRadius: '100px', bgcolor: colors.primary, fontFamily: '"DM Sans", sans-serif', '&:hover': { bgcolor: colors.primaryDark } }}
                >Scanner Google</Button>
              </Box>
            </GlassCard>
          )}
        </Box>
      </TabPanel>

      {/* ==========================================
          TAB 4: REPONDRE AUX MENTIONS
          ========================================== */}
      <TabPanel value={activeTab} index={4}>
        <Box>
          <Typography variant="body2" sx={{ color: colors.textMed, mb: 3, fontFamily: '"DM Sans", sans-serif' }}>
            Generez des reponses IA personnalisees pour vos mentions recentes.
          </Typography>

          {mentionsLoading ? (
            <>{[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 2, borderRadius: '16px' }} />)}</>
          ) : recentMentions.length === 0 ? (
            <GlassCard glow={false}>
              <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                <Box sx={{ width: 88, height: 88, borderRadius: '50%', bgcolor: colors.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                  <ReplyIcon sx={{ fontSize: 40, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ color: colors.textMed, mb: 1, fontFamily: '"DM Sans", sans-serif', textWrap: 'balance' }}>Aucune mention recente</Typography>
                <Typography variant="body2" sx={{ color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }}>Les mentions apparaitront ici apres la collecte.</Typography>
              </Box>
            </GlassCard>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentMentions.map((mention) => (
                <GlassCard key={mention.id} glow={false} hover>
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip label={mention.platform} size="small" sx={{ fontSize: '0.68rem', height: 18, borderRadius: '100px', bgcolor: colors.primaryBg, color: colors.primary }} />
                          <Chip label={mention.sentiment || 'neutral'} size="small"
                            sx={{ fontSize: '0.68rem', height: 18, borderRadius: '100px', bgcolor: sentimentColor(mention.sentiment) + '15', color: sentimentColor(mention.sentiment) }}
                          />
                          <Typography variant="caption" sx={{ color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }}>
                            {mention.author_name || 'Anonyme'}{mention.author_followers > 0 ? ` (${mention.author_followers} abonnes)` : ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: colors.charcoal, lineHeight: 1.6, fontFamily: '"DM Sans", sans-serif', wordBreak: 'break-word' }}>
                          {(mention.text || '').substring(0, 300)}{(mention.text || '').length > 300 ? '...' : ''}
                        </Typography>
                        {mention.published_at && (
                          <Typography variant="caption" sx={{ color: colors.textLight, fontFamily: '"DM Sans", sans-serif', mt: 0.5, display: 'block' }}>
                            {new Date(mention.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Generated reply */}
                    {generatedReplies[mention.id] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: colors.ivory, borderRadius: '12px', border: `1px solid ${colors.borderLight}`, position: 'relative' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: colors.primary, display: 'block', mb: 0.5, fontFamily: '"DM Sans", sans-serif' }}>Reponse generee</Typography>
                        <Typography variant="body2" sx={{ color: colors.charcoal, whiteSpace: 'pre-wrap', fontFamily: '"DM Sans", sans-serif', pr: 4, lineHeight: 1.6 }}>
                          {generatedReplies[mention.id]}
                        </Typography>
                        <Tooltip title={copiedId === `reply-${mention.id}` ? 'Copie !' : 'Copier'}>
                          <IconButton aria-label="Copier la reponse" size="small" onClick={() => handleCopyMessage(generatedReplies[mention.id], `reply-${mention.id}`)}
                            sx={{ position: 'absolute', top: 8, right: 8, color: copiedId === `reply-${mention.id}` ? colors.success : colors.textLight }}
                          >
                            {copiedId === `reply-${mention.id}` ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined"
                        onClick={() => handleGenerateReply(mention)}
                        disabled={replyGenerating === mention.id}
                        startIcon={replyGenerating === mention.id ? <CircularProgress size={14} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '100px', borderColor: colors.primary, color: colors.primary, fontFamily: '"DM Sans", sans-serif' }}
                      >
                        {replyGenerating === mention.id ? 'Generation...' : generatedReplies[mention.id] ? 'Regenerer' : 'Generer reponse'}
                      </Button>
                      {mention.url && (
                        <Button size="small" variant="text" component="a" href={mention.url} target="_blank" rel="noopener noreferrer"
                          sx={{ textTransform: 'none', fontSize: '0.75rem', color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }}
                        >Voir la source</Button>
                      )}
                    </Box>
                  </Box>
                </GlassCard>
              ))}
            </Box>
          )}
        </Box>
      </TabPanel>

      {/* ==========================================
          TAB 5: RELATIONS PRESSE
          ========================================== */}
      <TabPanel value={activeTab} index={5}>
        <Box>
          {/* ── Influence Discovery Panel ── */}
          <GlassCard glow={false} sx={{ mb: 3 }}>
            <Box sx={{ p: 3 }}>
              <Typography sx={{
                fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem',
                fontWeight: 600, color: colors.charcoal,
                textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5,
              }}>
                Influenceurs detectes
              </Typography>
              <Typography sx={{
                fontSize: '0.72rem', color: colors.textLight,
                fontFamily: '"DM Sans", sans-serif', mb: 2,
              }}>
                Auteurs les plus actifs dans vos mentions — cliquez pour les ajouter a vos contacts
              </Typography>

              {influenceLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} sx={{ color: colors.primary }} />
                </Box>
              ) : influencers.length === 0 ? (
                <Typography sx={{
                  fontSize: '0.78rem', color: colors.textLight,
                  fontFamily: '"DM Sans", sans-serif', fontStyle: 'italic', py: 2, textAlign: 'center',
                }}>
                  Pas assez de donnees pour cartographier l'ecosysteme
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {influencers.slice(0, 10).map((inf, idx) => (
                    <Box
                      key={`${inf.author_username}-${idx}`}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        p: 1.5, borderRadius: '10px',
                        bgcolor: 'rgba(27,94,64,0.03)',
                        border: `1px solid ${colors.borderLight}`,
                        '&:hover': { bgcolor: 'rgba(27,94,64,0.06)' },
                        transition: 'background 0.2s',
                      }}
                    >
                      {/* Score circle */}
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: colors.primaryBg,
                        border: `2px solid ${colors.primary}`,
                        flexShrink: 0,
                      }}>
                        <Typography sx={{
                          fontSize: '0.68rem', fontWeight: 700,
                          color: colors.primary, fontFamily: '"DM Sans", sans-serif',
                        }}>
                          {Math.round(inf.influence_score)}
                        </Typography>
                      </Box>

                      {/* Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, flexWrap: 'wrap' }}>
                          <Typography sx={{
                            fontSize: '0.8rem', fontWeight: 600,
                            color: colors.text, fontFamily: '"DM Sans", sans-serif',
                          }}>
                            {inf.author_name || inf.author_username}
                          </Typography>
                          <Chip label={inf.category} size="small" sx={{
                            height: 18, fontSize: '0.6rem', fontFamily: '"DM Sans", sans-serif',
                            bgcolor: colors.primaryBg, color: colors.primary,
                            border: `1px solid ${colors.borderLight}`,
                          }} />
                          <Chip label={inf.platform} size="small" sx={{
                            height: 18, fontSize: '0.6rem', fontFamily: '"DM Sans", sans-serif',
                            bgcolor: 'transparent', color: colors.textLight,
                            border: `1px solid ${colors.borderLight}`,
                          }} />
                        </Box>
                        <Typography sx={{
                          fontSize: '0.68rem', color: colors.textMed,
                          fontFamily: '"DM Sans", sans-serif',
                        }}>
                          {inf.mention_count} mention{inf.mention_count > 1 ? 's' : ''} · {inf.max_followers?.toLocaleString() || 0} followers · sentiment {inf.avg_sentiment > 0 ? '+' : ''}{(inf.avg_sentiment * 100).toFixed(0)}%
                        </Typography>
                      </Box>

                      {/* Add to contacts */}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                            const params = new URLSearchParams({
                              author_username: inf.author_username,
                              author_name: inf.author_name || inf.author_username,
                              platform: inf.platform || '',
                              influence_score: String(inf.influence_score),
                              category: inf.category || 'autre',
                              mention_count: String(inf.mention_count || 0),
                              avg_sentiment: String(inf.avg_sentiment || 0),
                            });
                            await fetch(`${API}/api/influence/add-contact/${selectedBrand}?${params}`, {
                              method: 'POST',
                              headers: token ? { Authorization: `Bearer ${token}` } : {},
                            });
                            setSuccess('Contact ajouté');
                            fetchPressContacts(true);
                          } catch (err) { console.warn('add contact error', err); }
                        }}
                        sx={{
                          fontSize: '0.65rem', fontFamily: '"DM Sans", sans-serif',
                          color: colors.primary, borderColor: colors.borderLight,
                          borderRadius: '100px', textTransform: 'none', px: 1.5,
                          flexShrink: 0,
                          '&:hover': { borderColor: colors.primary, bgcolor: colors.primaryBg },
                        }}
                      >
                        + Contact
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </GlassCard>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="body2" sx={{ color: colors.textMed, fontFamily: '"DM Sans", sans-serif' }}>
              Gerez vos contacts presse et medias pour {brandName}.
            </Typography>
            <Button variant="contained" size="small"
              onClick={() => handleOpenPressDialog()}
              startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', borderRadius: '100px', bgcolor: colors.primary, fontFamily: '"DM Sans", sans-serif', '&:hover': { bgcolor: colors.primaryDark } }}
            >
              Ajouter un contact
            </Button>
          </Box>

          {pressLoading ? (
            <Skeleton variant="rounded" height={200} sx={{ borderRadius: '16px' }} />
          ) : pressContacts.length === 0 ? (
            <GlassCard glow={false}>
              <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                <Box sx={{ width: 88, height: 88, borderRadius: '50%', bgcolor: colors.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                  <ContactsIcon sx={{ fontSize: 40, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ color: colors.textMed, mb: 1, fontFamily: '"DM Sans", sans-serif', textWrap: 'balance' }}>Aucun contact personnalise</Typography>
                <Typography variant="body2" sx={{ color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }}>Ajoutez vos contacts medias, influenceurs et partenaires via le bouton ci-dessus.</Typography>
              </Box>
            </GlassCard>
          ) : (
            <GlassCard glow={false}>
              <Box sx={{ p: 3 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', borderColor: colors.borderLight }}>Nom</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', borderColor: colors.borderLight }}>Media</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', borderColor: colors.borderLight }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', borderColor: colors.borderLight }}>Telephone</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', borderColor: colors.borderLight }}>Dernier contact</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', borderColor: colors.borderLight }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pressContacts.map((contact) => (
                        <TableRow key={contact.id} sx={{ '& td': { borderColor: colors.borderLight } }}>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 600 }}>{contact.name}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif' }}>{contact.media || '\u2014'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif' }}>
                            {contact.email ? <a href={`mailto:${contact.email}`} style={{ color: colors.primary, textDecoration: 'none' }}>{contact.email}</a> : '\u2014'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif' }}>{contact.phone || '\u2014'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif' }}>
                            {contact.last_contact_date ? new Date(contact.last_contact_date).toLocaleDateString('fr-FR') : '\u2014'}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton aria-label="Modifier le contact" size="small" onClick={() => handleOpenPressDialog(contact)} sx={{ color: colors.primary }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton aria-label="Supprimer le contact" size="small" onClick={() => handleDeletePressContact(contact.id)} sx={{ color: colors.danger }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </GlassCard>
          )}
        </Box>
      </TabPanel>

      {/* Workflow Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
      >
        {Object.entries(WORKFLOW_STATUS_CONFIG).map(([key, cfg]) => (
          <MenuItem key={key} onClick={() => handleStatusMenuSelect(key)}
            sx={{ fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif', gap: 1 }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.color }} />
            {cfg.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => {
          if (confirmDialog.onConfirm) confirmDialog.onConfirm();
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }}
        onCancel={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
      />

      {/* Press Contact Dialog */}
      <Dialog
        open={pressDialogOpen}
        onClose={() => setPressDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 600 }}>
          {pressEditId ? 'Modifier le contact' : 'Ajouter un contact presse'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Nom" value={pressForm.name} onChange={(e) => setPressForm(prev => ({ ...prev, name: e.target.value }))} fullWidth required />
            <TextField label="Media / Organisation" value={pressForm.media} onChange={(e) => setPressForm(prev => ({ ...prev, media: e.target.value }))} fullWidth placeholder="Ex: Jeune Afrique, RFI, Le Monde..." />
            <TextField label="Email" value={pressForm.email} onChange={(e) => setPressForm(prev => ({ ...prev, email: e.target.value }))} fullWidth type="email" />
            <TextField label="Telephone" value={pressForm.phone} onChange={(e) => setPressForm(prev => ({ ...prev, phone: e.target.value }))} fullWidth />
            <TextField label="Notes" value={pressForm.notes} onChange={(e) => setPressForm(prev => ({ ...prev, notes: e.target.value }))} fullWidth multiline rows={2} placeholder="Informations supplementaires..." />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPressDialogOpen(false)} sx={{ fontFamily: '"DM Sans", sans-serif', textTransform: 'none' }}>Annuler</Button>
          <Button variant="contained" onClick={handleSavePressContact} disabled={!pressForm.name.trim()}
            sx={{ bgcolor: colors.primary, borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', textTransform: 'none', '&:hover': { bgcolor: colors.primaryDark } }}
          >
            {pressEditId ? 'Mettre a jour' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AgirPage;
